import Layout from "@/layouts/layout";
import Card from "@/components/ui/Card";
import EmptyState from "@/components/ui/EmptyState";
import Panel from "@/components/ui/Panel";
import { ButtonLink } from "@/components/ui/Button";
import PageHeading from "@/components/layout/PageHeading";
import type { SatelliteEvent } from "@/interfaces/satellite_event";
import type { Submission } from "@/interfaces/submission";

// What the room made. Public, and deliberately first-names-only: the people in
// here are teenagers and the page is open to anyone with the link.
//
// Approved hand-ins only, with one exception: the viewer's own. Somebody who
// just handed in and finds the wall unchanged concludes it didn't save and
// does it again.
export default function EventSubmissions({
  event,
  submissions,
  mine,
  can_submit,
  submissions_open,
}: {
  event: SatelliteEvent;
  submissions: Submission[];
  mine: Submission | null;
  can_submit: boolean;
  submissions_open: boolean;
}) {
  const eventPath = `/events/${event.slug ?? event.id}`;
  const minePending = mine != null && mine.review_state !== "approved";

  return (
    <Layout>
      <div className="flex w-full flex-col gap-6 py-6">
        <PageHeading
          title={`Built at ${event.name}`}
          subtitle={
            submissions.length === 1
              ? "1 site on the wall."
              : `${submissions.length} sites on the wall.`
          }
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <ButtonLink href={eventPath} variant="secondary" size="sm">
                Back to the event
              </ButtonLink>
              {submissions_open && (
                <ButtonLink href={`${eventPath}/submit`} size="sm">
                  {can_submit ? "Hand in yours" : "Hand in your site"}
                </ButtonLink>
              )}
            </div>
          }
        />

        {minePending && mine && <MineInReview submission={mine} />}

        {submissions.length === 0 ? (
          <EmptyState
            title="Nothing on the wall yet"
            description={
              !submissions_open
                ? "Check back after the event."
                : event.submissions_reviewed
                  ? "Sites go up once someone has looked at them. Be the first: put yours somewhere with a URL, then hand it in."
                  : "Be the first. Put your site somewhere with a URL, then hand it in."
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {submissions.map((submission) => (
              <SubmissionCard key={submission.id} submission={submission} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

// The author's own hand-in while it waits on a verdict, or after it's been
// sent back. Nobody else sees this panel, and nobody else sees the note.
function MineInReview({ submission }: { submission: Submission }) {
  const sentBack = submission.review_state === "rejected";

  return (
    <Panel title={sentBack ? "have-another-go.txt" : "yours.txt"} tone="alt">
      <div className="flex flex-col gap-3">
        <h2 className="font-display text-subheading text-ink leading-tight font-bold">
          {sentBack
            ? `${submission.title} came back`
            : `${submission.title} is in`}
        </h2>
        <p className="text-body text-ink-muted">
          {sentBack
            ? "Someone looked at it and sent it back. Fix it up and hand it in again - a second hand-in replaces the first, so there's nothing to undo."
            : "Someone will look at it and email you. It goes up on the wall once it's approved."}
        </p>
        {submission.review_notes && (
          <p className="text-body text-ink border-line bg-surface-alt border-l-4 py-2 pl-3 whitespace-pre-line">
            {submission.review_notes}
          </p>
        )}
      </div>
    </Panel>
  );
}

function SubmissionCard({ submission }: { submission: Submission }) {
  return (
    <Card
      interactive
      media={
        submission.screenshot_url ? (
          <img
            src={submission.screenshot_url}
            alt={submission.title}
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
        ) : undefined
      }
    >
      <div className="flex flex-1 flex-col gap-2">
        <h2 className="font-display text-subheading text-ink leading-tight font-bold">
          {submission.title}
        </h2>
        <p className="text-caption text-ink-soft">by {submission.author}</p>
        {submission.description && (
          <p className="text-body text-ink-muted line-clamp-4">
            {submission.description}
          </p>
        )}
        <a
          // Someone else's site, typed in by a teenager: opened in a new tab,
          // and never handed this page's window or referrer.
          href={submission.live_url}
          target="_blank"
          rel="noopener noreferrer nofollow ugc"
          className="text-link text-body mt-auto pt-2 font-bold break-all underline"
        >
          Visit the site
        </a>
      </div>
    </Card>
  );
}
